import { FC } from 'react'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { GuildScore } from '@/atoms/GuildScore'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { slugs } from '@/constants/slugs'
import { PortraitRowCollection } from '@/molecules/PortraitRowCollection'
import {
  buildLinkTabModel,
  buildLinkTabUrl,
  buildTabSubtitle,
  ComingSoonNode,
  shortenMonthInHeader,
} from '@/services/ComingSoonService'
import { useGuildMember } from '@/services/GuildUserService'
import { useTranslate } from '@/utils/translate/translate-client'
import { LargePortraitLink } from '../CollectionDisplay/LargePortraitLink'

interface ComingSoonCollectionProps {
  title: string
  ComingSoonTabCollection?: ComingSoonNode[]
  testId?: string
  layout?: 'portrait-grid' | 'portrait-row'
}

export const ComingSoonCollection: FC<ComingSoonCollectionProps> = ({
  ComingSoonTabCollection,
  testId,
  layout = 'portrait-row',
}) => {
  const { t } = useTranslate('watch')
  const { isGuildMember } = useGuildMember()

  const canDisplay = ComingSoonTabCollection && ComingSoonTabCollection?.length > 0
  const plannedTheatricalReleases = new Set([
    slugs.bull,
    slugs.fablehaven,
    slugs.fellowship,
    slugs.jacob,
    slugs.oldManInTheDunes,
    slugs.senior,
    slugs.soloMio,
    slugs.strangersCase,
    slugs.youngWashington,
    slugs.zeroAd,
  ])

  return (
    <>
      <When condition={canDisplay && layout === 'portrait-row'}>
        <LazyHydrate whenVisible>
          <div className="flex flex-col gap-y-12">
            <PortraitRowCollection
              testId={testId}
              title={t('comingSoonAngel', 'Coming Soon to Angel')}
              slidesToScroll={6}
              slidesToShow={6}
              haltLazyLoad
            >
              {ComingSoonTabCollection?.map((item) => {
                if (!item) return null

                if (
                  item?.watchable?.__typename !== 'ContentMovie' &&
                  item?.watchable?.__typename !== 'ContentSeries' &&
                  item?.watchable?.__typename !== 'ContentSpecial' &&
                  item?.watchable?.__typename !== 'ContentSeason'
                ) {
                  return null
                }

                if (
                  item?.focus?.__typename === 'TheatricalTicketsSaleFocus' ||
                  (item?.project?.slug && plannedTheatricalReleases.has(item?.project?.slug))
                ) {
                  return null
                }

                const linkModel = buildLinkTabModel(item, true)
                if (!linkModel) return null

                const title = item.title ?? item?.watchable?.name ?? item?.project?.name
                const subTitle = buildTabSubtitle(item, t)

                const header = item.header && shortenMonthInHeader(item.header)

                return (
                  <LazyHydrate key={item?.id} whenVisible>
                    <>
                      <div className="mb-2 mr-4">
                        <LargePortraitLink
                          link={{ ...linkModel, linkUrl: buildLinkTabUrl(item) }}
                          isGuildMember={isGuildMember}
                        />
                      </div>
                      <TitleAndSubtitleDisplay
                        className="mr-4"
                        title={title || undefined}
                        subtitle={header ? `${header}` : subTitle}
                      />
                      <When condition={item?.project?.highestScore}>
                        <GuildScore guildScore={item.project.highestScore} />
                      </When>
                    </>
                  </LazyHydrate>
                )
              })}
            </PortraitRowCollection>
            <PortraitRowCollection
              testId={testId}
              title={t('upcomingTheatricalReleases', 'Upcoming Theatrical Releases')}
              slidesToScroll={6}
              slidesToShow={6}
              haltLazyLoad
            >
              {ComingSoonTabCollection?.map((item) => {
                if (!item) return null

                if (
                  item?.watchable?.__typename !== 'ContentMovie' &&
                  item?.watchable?.__typename !== 'ContentSeries' &&
                  item?.watchable?.__typename !== 'ContentSpecial' &&
                  item?.watchable?.__typename !== 'ContentSeason'
                ) {
                  return null
                }

                if (
                  item?.focus?.__typename !== 'TheatricalTicketsSaleFocus' &&
                  item?.project?.slug &&
                  !plannedTheatricalReleases.has(item?.project?.slug)
                ) {
                  return null
                }

                const linkModel = buildLinkTabModel(item, true)
                if (!linkModel) return null

                const title = item.title ?? item?.watchable?.name ?? item?.project?.name
                const subTitle = buildTabSubtitle(item, t)

                const header = item.header && shortenMonthInHeader(item.header)

                return (
                  <LazyHydrate key={item?.id} whenVisible>
                    <>
                      <div className="mb-2 mr-4">
                        <LargePortraitLink
                          link={{ ...linkModel, linkUrl: buildLinkTabUrl(item) }}
                          isGuildMember={isGuildMember}
                        />
                      </div>
                      <TitleAndSubtitleDisplay
                        className="mr-4"
                        title={title || undefined}
                        subtitle={header ? `${header}` : subTitle}
                      />
                      <When condition={item?.project?.highestScore}>
                        <GuildScore guildScore={item.project.highestScore} />
                      </When>
                    </>
                  </LazyHydrate>
                )
              })}
            </PortraitRowCollection>
          </div>
        </LazyHydrate>
      </When>
    </>
  )
}
